import React, { useCallback, useEffect, useState, useMemo } from "react";
import prop from 'ramda/es/prop';
import styled from "@emotion/styled";
import { Col, Row } from "react-bootstrap";
import { Checkbox } from "../../../UI/Checkbox";
import { useParams } from "react-router";
import { useNetworkContext } from "../../../contexts/NetworkContext/context";
import { GetAllNetworksResponse, Network, L3 } from "../../../services/NetworkApi";
import { Table } from "../../../UI/Table";
import { TableCell } from "../../../UI/TableCell";
import { TableHead } from "../../../UI/TableHead";
import { TableRow } from "../../../UI/TableRow";
import { Typography } from "../../../UI/Typography";
import { Button } from "../../../UI/Button";
import { Modal } from "../../../components/Modal";
import { Grid } from "../../../UI/Grid";
import useAuth from "../../../routes/login/AuthContext";
import { useModalContext } from "../../../contexts/ModalContext/context";
import { ModalProvider } from "../../../contexts/ModalContext";
import {
  AssignTemplateToL3,
  GetLifestylePortfolioTemplates, RemoveTemplateFromL3
} from "../../../services/PortfolioApi";
import { Select } from "../../../UI/Select";
import { SelectWrapper } from "../../../components/SelectWrapper";

const StyledCol = styled(Col)`
  padding: 0!important;
`;
const StyledRow = styled(Row)`
  margin-top: 36px;
  align-items: center;
  margin-left: 0px;
  width: 100%;
  border: ${prop('border')};
  border-radius: 8px;
  padding: ${prop('padding')};
`;
const StyledRowSimple = styled(Row)`
  align-items: center;
  margin-left: 0px;
  label {
    margin-bottom: 0px;
  }
`;
const StyledButton = styled(Button)`
  margin-left: auto;
  width: 200px;
`;
const SubnetworkLabel = styled(Typography)`
  text-align: center;
  margin-top: 48px;
  margin-bottom: 24px;
  width: 100%;
`;

interface TemplateNetworkChooserProps { 
  showArchived: boolean;
}

interface SelectedNetworkProps {
  network: Network;
  l3: L3;
}

interface SelectAllSubNetwork {
  [key: string]: boolean;
}


export const TemplateNetworkChooser: React.FC<TemplateNetworkChooserProps> = (props) => {
  const { getAuthToken } = useAuth();
  const { loadNetworkInfoForNetworks } = useNetworkContext();
  const { networkId, portfolioId } = useParams<{ networkId: string, portfolioId: string }>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedNetworks, setSelectedNetworks] = useState<string[]>([]);
  const [selectedNetwork, setSelectedNetwork] = useState<SelectedNetworkProps>();
  const [templates, setTemplates] = useState<{ value: number; label: string; }[]>([]);
  const [networkInfo, setNetworkInfo] = useState<undefined | GetAllNetworksResponse>(undefined);
  const [selectedTemplate, setSelectedTemplate] = useState<any>({ value: selectedNetwork?.l3?.lifestyleTemplateSelection });
  const [ selectAllInSubNetwork, setSelectAllInSubNetwork] = useState<SelectAllSubNetwork>({});


  const isAnySelected = !!networkInfo && selectedNetworks.length > 0;

  const load = async () => {
    const loadedNetworkInfo = await loadNetworkInfoForNetworks([parseInt(networkId)]);
    setNetworkInfo(loadedNetworkInfo);

    if(loadedNetworkInfo?.networks) {
      const selectedAllSubNetworksTemplate: SelectAllSubNetwork = loadedNetworkInfo.networks.reduce((acc, subnetwork) => {
        return {
        ...acc,
          [`${subnetwork.l1Id}-${subnetwork.l2Id}`]: false
        };
      }, {});
      setSelectAllInSubNetwork(selectedAllSubNetworksTemplate);
    }
   
    const templatesRequest = await GetLifestylePortfolioTemplates(portfolioId, await getAuthToken());

    if (templatesRequest) {
      setTemplates(templatesRequest.lifestyleTemplates.map(template => ({ value: template.id, label: template.name })));
    }
  };

  useEffect(() => {
    load();
  }, [networkId]);

  const onPress = (network: Network, l3: L3) => {
    setSelectedNetwork({ network, l3 });
  };

  const clearSelectedNetwork = () => {
    setSelectedNetwork(undefined);
    load();
  };

  const onSelectAll = (subnetwork: Network) => {
    
    if (selectAllInSubNetwork[`${subnetwork.l1Id}-${subnetwork.l2Id}`]) {
      const filteredNetworks = selectedNetworks.filter(s => s.search(`${subnetwork.l1Id}-${subnetwork.l2Id}`));
      setSelectedNetworks(filteredNetworks);
    } else if (selectAllInSubNetwork[`${subnetwork.l1Id}-${subnetwork.l2Id}`] === false) {
      const selectedL3s = subnetwork.l3.map(l3 => `${subnetwork.l1Id}-${subnetwork.l2Id}-${l3.l3Id}`);
      setSelectedNetworks([...selectedNetworks ,...selectedL3s]);
    }
    setSelectAllInSubNetwork({
    ...selectAllInSubNetwork,
      [`${subnetwork.l1Id}-${subnetwork.l2Id}`]:!selectAllInSubNetwork[`${subnetwork.l1Id}-${subnetwork.l2Id}`]
    });
  };

  const onSelectCheckbox = (id: string) => {
    if (selectedNetworks.includes(id)) {
      setSelectedNetworks(selectedNetworks.filter(networkId => networkId !== id));
    } else {
      setSelectedNetworks([...selectedNetworks, id]);
    }
  };

  const nonArchivedNetworks = useMemo(() => networkInfo?.networks.filter(n => n.matchingRoundStatus !== "ARCHIVED").length, [networkInfo]);

  const showRow = (l2: Network) => {
    if(props.showArchived) {
      return true;
    }
    return !props.showArchived && l2?.matchingRoundStatus !== "ARCHIVED";
  };

  const onClearTemplate = async (l2Id: string, l3Id: string) => {
    await RemoveTemplateFromL3(networkId, l2Id, l3Id, await getAuthToken());

    await load();
    setIsLoading(false);
  };

  const onSubmit = useCallback(async () => {
    if (selectedNetworks.length) {
      setIsLoading(true);
      const units = selectedNetworks.map(network => {
        const [l1Id, l2Id, l3Id] = network.split('-');

        return { l1Id, l2Id, l3Id };
      });

      await AssignTemplateToL3(networkId, selectedTemplate?.value, portfolioId, units, await getAuthToken());

      await load();
      setIsLoading(false);
    }
  }, [selectedTemplate, selectedNetworks, networkId]);

  if (!networkInfo) {
    return <div>Loading</div>;
  }

  return (
    <StyledCol>
      {
        networkInfo.networks.map(
          subnetwork => showRow(subnetwork) && (
            <StyledCol key={"subnetwork" + subnetwork.l2Id}>
              <SubnetworkLabel variant="h6" fontWeight={600}>
                {`SUBNETWORK: ${subnetwork.l2Name}`}
              </SubnetworkLabel>
              <Typography mb={1} variant="subtitle1" fontWeight={500}>
                Lifestyle Questions
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                    {subnetwork.matchingRoundStatus !== "ARCHIVED" && (
                      <StyledRowSimple>
                        <Checkbox
                          name="accept"
                          isChecked={selectAllInSubNetwork[`${subnetwork.l1Id}-${subnetwork.l2Id}`]}
                          onChange={() => onSelectAll(subnetwork)}
                        />
                        Select All
                      </StyledRowSimple>)}
                    </TableCell>
                    <TableCell>Unit Type</TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                {
                  subnetwork.l3.map(l3 => (
                    <TableRow key={"l3" + l3.l3Id}>
                      <TableCell>
                      {subnetwork.matchingRoundStatus !== "ARCHIVED" && (
                        <Checkbox
                          name="accept"
                          isChecked={selectedNetworks.includes(`${subnetwork.l1Id}-${subnetwork.l2Id}-${l3.l3Id}`)}
                          onChange={() => onSelectCheckbox(`${subnetwork.l1Id}-${subnetwork.l2Id}-${l3.l3Id}`)}
                        />)}
                      </TableCell>
                      <TableCell>{l3.name}</TableCell>
                      <TableCell>{l3.lifestyleTemplateSelection?.name || 'NONE LINKED'}</TableCell>
                      <TableCell align='right' colSpan={1}>
                        {subnetwork.matchingRoundStatus !== 'ARCHIVED' && l3.lifestyleTemplateSelection &&
                          <Button
                            onClick={() => onClearTemplate(""+subnetwork.l2Id, ""+l3.l3Id)}
                            appearance={'danger'}
                          >
                            Clear
                          </Button>
                        }
                        {subnetwork.matchingRoundStatus !== 'ARCHIVED' &&
                          <Button
                            onClick={() => onPress(subnetwork, l3)}
                            appearance={l3.lifestyleTemplateSelection ? "primary" : 'warning'}
                          >
                            {l3.lifestyleTemplateSelection ? 'Update' : 'Set'}
                          </Button>
                        }
                        
                      </TableCell>
                    </TableRow>
                  ))
                }
              </Table>
            </StyledCol>
          )
        )
      }
      { !!nonArchivedNetworks &&
      <StyledRow border={`1.5px solid ${isAnySelected ? 'black' : 'gray'}`} padding="12px 16px">
        <Typography variant="body1" gutterBottom mr={1} mb={0} color={isAnySelected ? 'black' : 'gray'}>
          Lifestyle questions template for selected unit types:
        </Typography>
        <SelectWrapper>
          <Select
            id="network-template-select"
            value={selectedTemplate}
            isDisabled={!isAnySelected}
            onChange={setSelectedTemplate}
            options={templates}
          />
        </SelectWrapper>
        <StyledButton
          onClick={onSubmit}
          appearance={"primary"}
          isLoading={isLoading}
          isDisabled={!selectedTemplate?.value || !isAnySelected}
        >
          Save
        </StyledButton>
      </StyledRow>}
      <ModalProvider>
        <UpdateLifeStyleModal
          templates={templates}
          open={!!selectedNetwork}
          portfolioId={portfolioId}
          networkId={networkId}
          selectedNetwork={selectedNetwork}
          clearSelectedNetwork={clearSelectedNetwork}
        />
      </ModalProvider>
    </StyledCol>
  );
};

const UpdateLifeStyleModal = ({ networkId, portfolioId, open, templates, clearSelectedNetwork, selectedNetwork }: LifestyleModalActionProps) => {
  const { setIsOpen } = useModalContext();
  const { getAuthToken } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>({});

  const disabled = selectedNetwork?.l3?.lifestyleTemplateSelection?.id === selectedTemplate?.value || !selectedTemplate?.value;

  React.useEffect(() => {
    setIsOpen(open);
    selectedNetwork?.l3?.lifestyleTemplateSelection && setSelectedTemplate({
      value: selectedNetwork?.l3?.lifestyleTemplateSelection.id,
      label: selectedNetwork?.l3?.lifestyleTemplateSelection.name
    });
  }, [open]);

  const onClose = useCallback(() => {
    setIsOpen(false);
    setIsLoading(false);
    setSelectedTemplate(null);
    clearSelectedNetwork();
  }, []);

  const onSubmit = useCallback(async () => {
    if (selectedNetwork) {
      setIsLoading(true);
      const units = [{ l1Id: networkId, l2Id: String(selectedNetwork.network.l2Id), l3Id: String(selectedNetwork?.l3.l3Id) }];
      await AssignTemplateToL3(networkId, selectedTemplate?.value, portfolioId, units, await getAuthToken());

      setIsLoading(false);
      onClose();
    }
  }, [selectedTemplate, selectedNetwork, networkId, portfolioId]);

  return (
    <Modal
      maxWidth="md"
      onClose={onClose}
      title={`${selectedNetwork?.l3?.lifestyleTemplateSelection ? 'Update' : 'Set'} Lifestyle Questions to Other Units`}
    >
      <Grid container spacing={2} px={3} width={706}>
        <Grid item xs={12} mt={2}>
          <Typography variant="body1" gutterBottom mt={2}>
            Network:<b>{` ${selectedNetwork?.network?.l1Name}`}</b>
          </Typography>
          <Typography variant="body1" gutterBottom mt={2}>
            Subnetwork:<b>{` ${selectedNetwork?.network?.l2Name}`}</b>
          </Typography>
          <Typography variant="body1" gutterBottom mt={2}>
            Unit Type:<b>{` ${selectedNetwork?.l3?.name}`}</b>
          </Typography>
          <StyledRow>
            <Typography variant="body1" gutterBottom mr={1}>
              Template:
            </Typography>
            <SelectWrapper>
              <Select
                id="network-template-select"
                value={selectedTemplate}
                onChange={setSelectedTemplate}
                options={templates}
              />
            </SelectWrapper>
          </StyledRow>
        </Grid>
        <Grid item xs={12} mb={4}>
          <Grid container justifyContent="flex-end">
            <Button
              onClick={onSubmit}
              appearance={"primary"}
              isLoading={isLoading}
              isDisabled={disabled || isLoading}
            >
              {selectedNetwork?.l3?.lifestyleTemplateSelection ? 'Update' : 'Set'}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  );
};

interface LifestyleModalActionProps {
  open: boolean;
  portfolioId: string;
  networkId: string;
  templates: { value: number; label: string; }[],
  clearSelectedNetwork: () => void,
  selectedNetwork: SelectedNetworkProps | undefined;
}
